import currentTimeZone from '../config/timeZone';

type MonthNameDataBase = { monthName: string };
type MonthDataBase = { month: string };
type DateDataBase = { date: string };

/**
 * Determines the chart aggregation bucket (hour/day/month) and day span for an
 * arbitrary custom date range, mirroring the granularity previously implied by
 * the fixed 'last24hours' | 'last7days' | 'lastMonth' | 'last12months' keys.
 * @param {string} startDate - Range start date (YYYY-MM-DD)
 * @param {string} endDate - Range end date (YYYY-MM-DD)
 * @returns {{ bucket: 'hour' | 'day' | 'month'; days: number }} The bucket type and number of days spanned
 */
export function getBucketFromRange(
  startDate: string,
  endDate: string,
): { bucket: 'hour' | 'day' | 'month'; days: number } {
  if (!startDate && !endDate) {
    return { bucket: 'month', days: 365 };
  }
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffDays =
    Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())
      ? 1
      : Math.max(1, Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1);

  if (diffDays <= 1) return { bucket: 'hour', days: diffDays };
  if (diffDays <= 60) return { bucket: 'day', days: diffDays };
  return { bucket: 'month', days: diffDays };
}

/**
 * Fills missing months in the last 12 months and sets missing numeric fields to 0.
 * Adds a `label` field formatted as `Month Year` (e.g., "Jan 2024").
 * @param {any} data - The data
 * @param {any} locale - The locale
 */
export function fillMissingMonthsGeneric<T extends MonthDataBase | MonthNameDataBase>(
  data: T[],
  locale: string = 'en-US',
): (Omit<T, 'monthName' | 'month'> & { label: string })[] {
  const today = new Date();
  const dateFormatter = new Intl.DateTimeFormat(locale, { month: 'short' });
  const yearFormatter = new Intl.DateTimeFormat(locale, { year: '2-digit' });
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const past12Months = [];
  for (let index = 0; index < 12; index += 1) {
    const date = new Date(today.getFullYear(), today.getMonth() - index, 1);
    const key = months[date.getMonth()];
    const label = dateFormatter.format(date);
    const year = yearFormatter.format(date);
    past12Months.push({ key, fullLabel: `${label} ${year}` });
  }

  const dataMap = new Map<string, T>();
  data.forEach((item) => {
    const key = (item as any).monthName ?? (item as any).month;
    if (key) dataMap.set(key, item);
  });

  // Get all unique keys (except monthName) to fill missing fields
  const fieldKeys = new Set<string>();
  data.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (key !== 'monthName' && key !== 'month') fieldKeys.add(key);
    });
  });

  const result = past12Months.map(({ key, fullLabel }) => {
    const existing = dataMap.get(key);
    if (existing) {
      const { monthName, month, ...rest } = existing as any;
      return { ...rest, label: fullLabel };
    }

    const filled: Record<string, any> = { label: fullLabel };
    fieldKeys.forEach((k) => {
      filled[k] = 0;
    });

    return filled as Omit<T, 'monthName' | 'month'> & { label: string };
  });

  return result.reverse();
}

/**
 * Fills missing days within an explicit date range, formatting each label as "MMM D" (e.g., "Sep 1"),
 * and filling missing numeric fields with 0.
 * @template T
 * @param {T[]} data - Array of data items, each with a `date` field (YYYY-MM-DD or ISO string)
 * @param {number} nDays - Number of days to fill (used only when rangeStart/rangeEnd are not provided)
 * @param {string} locale - Locale string for label formatting (defaults to 'en-US')
 * @param {string} [rangeStart] - Explicit range start date (YYYY-MM-DD). When provided, overrides nDays.
 * @param {string} [rangeEnd] - Explicit range end date (YYYY-MM-DD). When provided, overrides nDays.
 * @returns {Array} Array of data objects with formatted `label` and all numeric fields defaulted to 0
 */
export function fillMissingLastNDaysGeneric<T extends DateDataBase>(
  data: T[],
  nDays: number = 30,
  locale: string = 'en-US',
  rangeStart?: string,
  rangeEnd?: string,
): (Omit<T, 'date'> & { label: string })[] {
  const dateFormatter = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
  });

  // Build the ordered list of calendar days for the X-axis
  const days: Date[] = [];
  if (rangeStart && rangeEnd) {
    // Use the explicit range from the filter
    const parts = rangeStart.split('-');
    const current = new Date(
      parseInt(parts[0], 10),
      parseInt(parts[1], 10) - 1,
      parseInt(parts[2], 10),
    );
    const endParts = rangeEnd.split('-');
    const end = new Date(
      parseInt(endParts[0], 10),
      parseInt(endParts[1], 10) - 1,
      parseInt(endParts[2], 10),
    );
    while (current <= end) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
  } else {
    // Fallback: count nDays back from today
    const today = new Date();
    for (let index = nDays - 1; index >= 0; index -= 1) {
      const date = new Date(today);
      date.setDate(today.getDate() - index);
      days.push(date);
    }
  }

  // Lookup map for quick access
  const dataMap = new Map<string, T>();
  data.forEach((item) => {
    const iso = item.date.split('T')[0];
    dataMap.set(iso, item);
  });

  // Collect all keys except 'date'
  const fieldKeys = new Set<string>();
  data.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (key !== 'date') fieldKeys.add(key);
    });
  });

  return days.map((date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const isoDate = `${y}-${m}-${d}`;
    const existing = dataMap.get(isoDate);

    if (existing) {
      const { date: discardedDate, ...rest } = existing;
      return { ...rest, label: dateFormatter.format(date) };
    }

    const defaultItem: Record<string, any> = { label: dateFormatter.format(date) };
    fieldKeys.forEach((key) => {
      defaultItem[key] = 0;
    });

    return defaultItem as Omit<T, 'date'> & { label: string };
  });
}

/**
 * Fills missing data for the past 24 hours (or for a specific day's 24 hours) with default values (0), grouped by hour and date.
 * @param {any[]} data - Input data containing at least `date` (YYYY-MM-DD) and `hour` (0–23)
 * @param {string} [timeZone] - Optional time zone (defaults to browser's currentTimeZone)
 * @param {string} [locale] - The locale
 * @param {string} [targetDate] - Optional specific date (YYYY-MM-DD) to fill 24 hours for
 * @returns {any[]} Array of data objects, each with `label`, `hour`, and all other fields (defaulted to 0 if missing)
 */
export function fillMissingLast24HrsGeneric<T extends { date: string; hour: number | string }>(
  data: T[],
  timeZone: string = currentTimeZone,
  locale: string = 'en-US',
  targetDate?: string,
): Array<Omit<T, 'date' | 'hour'> & { label: string }> {
  const now = new Date();
  const past24Hours: { date: string; hour: number }[] = [];

  const cleanTargetDate = targetDate ? targetDate.split('T')[0] : undefined;
  const firstItemDate =
    data.length > 0 && data[0]?.date ? data[0].date.split('T')[0] : undefined;
  const isSingleDayData =
    Boolean(cleanTargetDate) ||
    Boolean(
      firstItemDate &&
        data.every((d) => d.date && d.date.split('T')[0] === firstItemDate),
    );
  const effectiveTargetDate = cleanTargetDate || (isSingleDayData ? firstItemDate : undefined);

  if (effectiveTargetDate) {
    for (let h = 0; h < 24; h += 1) {
      past24Hours.push({ date: effectiveTargetDate, hour: h });
    }
  } else {
    for (let index = 0; index < 24; index += 1) {
      const d = new Date(now);
      d.setHours(now.getHours() - index, 0, 0, 0);

      const dateString = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(d);

      past24Hours.push({ date: dateString, hour: d.getHours() });
    }
  }

  const dataMap = new Map(
    data.map((item) => {
      const d = item.date ? item.date.split('T')[0] : '';
      const h = Number(item.hour);
      return [`${d}_${h}`, item];
    }),
  );

  const allKeys = new Set<string>();
  data.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (key !== 'date' && key !== 'hour') allKeys.add(key);
    });
  });

  const hourFormatter = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    hour12: false,
  });

  const result = past24Hours.map(({ date, hour }) => {
    const key = `${date}_${Number(hour)}`;
    const existing = dataMap.get(key);

    // Create a date object to format the hour label according to locale
    const temporaryDate = new Date();
    temporaryDate.setHours(hour, 0, 0, 0);
    const formattedHour = hourFormatter.format(temporaryDate);
    const label =
      (existing as any)?.time ||
      `${String(hour).padStart(2, '0')}:00` ||
      formattedHour;

    if (existing) {
      const { date: unusedDate, hour: unusedHour, ...rest } = existing;
      return { label, ...rest } as any;
    }

    const fallback: Record<string, any> = { label };
    allKeys.forEach((k) => {
      fallback[k] = 0;
    });

    return fallback;
  });

  return effectiveTargetDate ? result : result.reverse();
}

/**
 * Fills missing data within an explicit date range with default values (0).
 * @template T
 * @param {T[]} data Input data containing at least `date` (YYYY-MM-DD or ISO string)
 * @param {number} nDays Number of days to fill (used only when rangeStart/rangeEnd are absent)
 * @param {string} locale Optional locale (defaults to 'en-US')
 * @param {string} [rangeStart] Explicit range start date (YYYY-MM-DD). Overrides nDays when provided.
 * @param {string} [rangeEnd] Explicit range end date (YYYY-MM-DD). Overrides nDays when provided.
 * @returns {Array} Array of data objects, each with `label` and all other fields (defaulted to 0 if missing)
 */
export function fillMissingLastNDaysGenericLatest<T extends DateDataBase>(
  data: T[],
  nDays: number = 30,
  locale: string = 'en-US',
  rangeStart?: string,
  rangeEnd?: string,
): (Omit<T, 'date'> & { label: string })[] {
  const dateFormatter = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
  });

  // Build the ordered list of calendar days for the X-axis
  const days: Date[] = [];
  let filterStart: Date;
  let filterEnd: Date;

  if (rangeStart && rangeEnd) {
    // Parse as local midnight to avoid UTC-shift
    const sp = rangeStart.split('-');
    const ep = rangeEnd.split('-');
    filterStart = new Date(parseInt(sp[0], 10), parseInt(sp[1], 10) - 1, parseInt(sp[2], 10));
    filterEnd = new Date(parseInt(ep[0], 10), parseInt(ep[1], 10) - 1, parseInt(ep[2], 10));
    const current = new Date(filterStart);
    while (current <= filterEnd) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
  } else {
    // Fallback: count nDays back from today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    filterStart = new Date(today);
    filterStart.setDate(today.getDate() - (nDays - 1));
    filterEnd = today;
    for (let index = nDays - 1; index >= 0; index -= 1) {
      const date = new Date(today);
      date.setDate(today.getDate() - index);
      days.push(date);
    }
  }

  const dataMap = new Map<string, T>();
  data.forEach((item) => {
    if (!item.date) return; // Skip null dates
    const itemDate = new Date(item.date);
    itemDate.setHours(0, 0, 0, 0);
    if (itemDate >= filterStart && itemDate <= filterEnd) {
      const y = itemDate.getFullYear();
      const mo = String(itemDate.getMonth() + 1).padStart(2, '0');
      const d = String(itemDate.getDate()).padStart(2, '0');
      dataMap.set(`${y}-${mo}-${d}`, item);
    }
  });

  const fieldKeys = new Set<string>();
  data.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (key !== 'date') fieldKeys.add(key);
    });
  });

  return days.map((date) => {
    const y = date.getFullYear();
    const mo = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const isoDate = `${y}-${mo}-${d}`;
    const existing = dataMap.get(isoDate);

    if (existing) {
      const { date: discardedDate, ...rest } = existing;
      return { ...rest, label: dateFormatter.format(date) };
    }

    const defaultItem: Record<string, any> = { label: dateFormatter.format(date) };
    fieldKeys.forEach((key) => {
      defaultItem[key] = 0;
    });

    return defaultItem as Omit<T, 'date'> & { label: string };
  });
}

/**
 * Maps raw backend chart data points to include a formatted label string without generating or filling missing intervals.
 * @param {any[]} data - The raw backend data array
 * @param {string} range - Time range ('last24hours' | 'last7days' | 'lastMonth' | 'last12months' | string)
 * @param {string} locale - Locale string for formatting
 * @returns {any[]} The data array with formatted label on each existing data point
 */
export function formatChartDataWithoutFilling(
  data: any[],
  range: string,
  locale: string = 'en-US',
): any[] {
  if (!Array.isArray(data)) return [];

  const dateFormatter = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
  });

  const monthFormatter = new Intl.DateTimeFormat(locale, {
    month: 'short',
  });

  const yearFormatter = new Intl.DateTimeFormat(locale, {
    year: '2-digit',
  });

  return data.map((item) => {
    if (item.label) {
      return item;
    }

    let label = '';

    if (
      range === 'last24hours' ||
      range === '1day' ||
      range === 'today' ||
      item.hour !== undefined ||
      item.time
    ) {
      if (item.time) {
        label = item.time;
      } else if (item.hour !== undefined && item.hour !== null) {
        const hourNumber = typeof item.hour === 'string' ? parseInt(item.hour, 10) : item.hour;
        label = `${String(hourNumber).padStart(2, '0')}:00`;
      } else if (item.date) {
        const dateObject = new Date(item.date);
        label = `${String(dateObject.getHours()).padStart(2, '0')}:00`;
      }
    } else if (range === 'last12months' || range === 'year') {
      if (item.monthName && item.year) {
        const yearString = String(item.year).slice(-2);
        label = `${item.monthName} ${yearString}`;
      } else if (item.monthName) {
        label = item.monthName;
      } else if (item.month) {
        if (typeof item.month === 'number') {
          const dateObject = new Date(item.year || 2000, item.month - 1, 1);
          const yearPart = item.year ? yearFormatter.format(dateObject) : '';
          label = `${monthFormatter.format(dateObject)} ${yearPart}`.trim();
        } else {
          label = String(item.month);
        }
      }
    } else if (item.date) {
      // Daily (last7days, lastMonth, 2-60 days, etc.)
      const parts = String(item.date).split('T')[0].split('-');
      if (parts.length === 3) {
        const dateObject = new Date(
          parseInt(parts[0], 10),
          parseInt(parts[1], 10) - 1,
          parseInt(parts[2], 10),
        );
        label = dateFormatter.format(dateObject);
      } else {
        const dateObject = new Date(item.date);
        label = dateFormatter.format(dateObject);
      }
    } else if (item.day !== undefined && item.month !== undefined && item.year !== undefined) {
      const dateObject = new Date(item.year, item.month - 1, item.day);
      label = dateFormatter.format(dateObject);
    }

    return {
      ...item,
      label: label || item.date || item.month || '',
    };
  });
}

